<template>
  <div class="contact-details">
    <label class="contact-details__inline-input">
      Bank account number
      <SoTextField
        :model-value="directDebit.accountNumber"
        data-testid="bank-account"
        type="number"
        :rules="generateRules(rules.required, rules.accountNumber)"
        @update:model-value="updateField('accountNumber', $event)"
      />
    </label>
    <label class="contact-details__inline-input">
      Sort code
      <SoTextField
        :model-value="directDebit.sortCode"
        data-testid="sort-code"
        :rules="generateRules(rules.required, rules.sortCode)"
        type="text"
        @update:model-value="updateField('sortCode', $event)"
      />
    </label>
    <label class="contact-details__inline-input">
      Name on account
      <SoTextField
        :model-value="directDebit.accountName"
        data-testid="account-name"
        :rules="generateRules(rules.required)"
        type="text"
        @update:model-value="updateField('accountName', $event)"
      />
    </label>
    <label class="contact-details__inline-input">
      Monthly payment amount
      <SoTextField
        prefix="£"
        :model-value="directDebit.amount"
        data-testid="monthly-payment-amount"
        :rules="generateRules(rules.required, rules.stringInteger)"
        type="number"
        @update:model-value="updateField('amount', $event)"
      />
    </label>
    <label class="contact-details__inline-input">
      Payment day
      <SoSelect
        :model-value="directDebit.nextPaymentDt"
        data-testid="payment-amount-dropdown"
        :items="paymentDayItems"
        :rules="generateRules(rules.required, rules.stringInteger)"
        @update:model-value="updateField('nextPaymentDt', $event)"
      />
    </label>
  </div>
  <div class="contact-details__inline-input">
    <v-spacer />
    <SoCheckbox
      :model-value="directDebit.seasonalPaymentFl"
      label="Seasonal payments"
      @update:model-value="updateField('seasonalPaymentFl', $event)"
    />
  </div>
  <div class="contact-details__inline-input">
    <v-spacer />
    <SoCheckbox
      :model-value="directDebit.hasAuthorisedDirectDebit"
      label="Is the customer authorised to set up Direct Debit??"
      :rules="generateRules(rules.required, rules.isTruthy)"
      @update:model-value="updateField('hasAuthorisedDirectDebit', $event)"
    />
  </div>
  <div class="contact-details__inline-input">
    <v-spacer />
    <SoCheckbox
      :model-value="directDebit.hasAcceptedTerms"
      label="Has the customer accepted the terms?"
      :rules="generateRules(rules.required, rules.isTruthy)"
      @update:model-value="updateField('hasAcceptedTerms', $event)"
    />
  </div>
</template>

<script lang="ts">
  import { defineComponent, PropType } from "vue"
  import { cloneDeep } from "lodash"
  import SoTextField from "@/components/formControls/SoTextField.vue"
  import SoCheckbox from "@/components/formControls/SoCheckbox.vue"
  import rules from "@/validators/rules"
  import { DirectDebitBankAccount } from "@/models/directDebit/directDebit.types"
  import { IPaymentScheduleRaw } from "@/models/paymentSchedule/paymentSchedule.parser"
  import SoSelect from "@/components/formControls/SoSelect.vue"
  import { getEmptyContact } from "@/models/contacts/contact"
  import { getMonthDays } from "@/helpers/dateHelper"
  import stringInteger from "@/validators/stringInteger"
  import { ValidationRule } from "@/types/ValidationRule"

  export default defineComponent({
    components: {
      SoSelect,
      SoTextField,
      SoCheckbox,
    },
    inheritAttrs: false,
    props: {
      modelValue: {
        type: Object as PropType<Record<string, string>>,
        default: () => getEmptyContact(),
      },
    },
    emits: ["update:modelValue"],
    data() {
      return {
        directDebit: {
          accountNumber: "",
          sortCode: "",
          accountName: "",
          amount: 0,
          nextPaymentDt: "",
          seasonalPaymentFl: false,
          hasAuthorisedDirectDebit: false,
          hasAcceptedTerms: false,
        } as Partial<DirectDebitBankAccount & IPaymentScheduleRaw>,
      }
    },
    computed: {
      rules() {
        return rules
      },
      isPartiallyFilled() {
        return (
          this.directDebit.accountNumber?.length ||
          this.directDebit.sortCode?.length ||
          this.directDebit.accountName?.length ||
          this.directDebit.nextPaymentDt?.length ||
          this.directDebit.amount
        )
      },
      paymentDayItems() {
        return getMonthDays().map((day) => ({
          title: `${day}`,
          value: `${day}`,
        }))
      },
    },
    created() {
      if (this.modelValue?.accountNumber || this.modelValue?.sortCode) {
        this.directDebit = {
          ...this.directDebit,
          ...cloneDeep(this.modelValue),
        }
      }
    },
    methods: {
      stringInteger,
      updateField(fieldName: string, fieldValue: string) {
        this.directDebit = { ...this.directDebit, [fieldName]: fieldValue }
        this.$emit("update:modelValue", this.directDebit)
      },
      generateRules(...ruleList: ValidationRule[]) {
        const dynamicRules = [] as ValidationRule[]
        if (this.isPartiallyFilled) {
          dynamicRules.push(...ruleList)
        }
        return dynamicRules
      },
    },
  })
</script>

<style scoped>
  .contact-details {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px;
  }
</style>
